import React, {useState} from "react";
import {connect} from "react-redux";
import {css} from "@panda/css";
import {bindActionCreators, Dispatch} from "redux";

import {ChevronDownIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {FormActions} from "@pg-mono/form";
import {head} from "@pg-mono/nodash";

import {IRPStore} from "../../../../app/rp_reducer";
import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {searchFormActions} from "../../../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../../../search/actions/set_active_dropdown_item";
import {ISearchInputValue} from "../../../../search/components/ISearchInputValue";
import {SearchAutocomplete} from "../../../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../../../search/constants/search_form_constants";
import {SearchTab} from "../../../../search/types/SearchTab";
import {IPromotionRegion, updatePromotionRegionList} from "../../../actions/fetch_promotion_list_region";
import {fetchPromotionSearchList} from "../../../actions/fetch_promotion_search_lists";
import {PromotionType} from "../../../helpers/PromotionType";
import {usePromotionListFilterChange} from "../../hooks/usePromotionListFilterChange";
import {PromotionListSearchFilterAutocompleteDropdown} from "./PromotionListSearchFilterAutocompleteDropdown";

/* eslint-disable @typescript-eslint/no-explicit-any */
export type IPromotionSearchFormValuesType = Record<"search", any>;

interface IStateProps {
    formValues: IPromotionSearchFormValuesType;
    latestQuery: Record<string, string | string[]>;
    promotionType: PromotionType | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchPromotionSearchList: (searchInput: string) => Promise<SearchTab>;
    updatePromotionRegionList: (region: IPromotionRegion | null) => {type: string; result: IPromotionRegion | null};
}

interface IPromotionListSearchFilter extends IStateProps, IActionsProps {}

const PromotionListSearchFilterC = (props: IPromotionListSearchFilter) => {
    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);

    const onPromotionListFilterChange = usePromotionListFilterChange();

    const onSubmit = async (formValues: IPromotionSearchFormValuesType) => {
        const searchValue = formValues["search"] as ISearchInputValue;

        if (searchValue.tabType === SearchTab.Regions) {
            onPromotionListFilterChange(props.promotionType || undefined, searchValue.regions?.[0]);
        }
    };

    const onAfterChange = (name?: string, value?: ISearchInputValue | number | undefined) => {
        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;

        //  submit only if user selected new region
        if (props.latestQuery.region === currentValues.search.regions?.[0]?.id) {
            return;
        }

        props.updatePromotionRegionList(head(currentValues.search?.regions) || null);

        return onSubmit(currentValues);
    };

    const onSearchSubmit = () => onSubmit(props.formValues);

    const selectedRegion = props.formValues?.search?.regions?.[0];
    const placeholder = "Miasto lub region";

    const renderSearchAutocomplete = (onDropdownStatusChange: React.Dispatch<React.SetStateAction<boolean>>, isInModal?: boolean) => {
        return (
            <div className={searchAutocompleteWrapperStyle}>
                <SearchAutocomplete
                    values={props.formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={onSearchSubmit}
                    onDropdownStatusChange={onDropdownStatusChange}
                    onAfterTabSwitch={props.resetActiveDropdownItem}
                    placeholder={placeholder}
                    disableMultiRegion
                    fetchAllSearchLists={props.fetchPromotionSearchList}
                    isInModal={isInModal}
                    autoFocus
                    dropdownWrapperClassName={dropdownWrapperStyle}
                    minWidth="100%"
                    renderDropdownList={(props) => (
                        <PromotionListSearchFilterAutocompleteDropdown
                            dropdownIsOpen={props.dropdownIsOpen}
                            search={props.search}
                            selectedTab={props.selectedTab}
                            onLinkClick={props.onLinkClick}
                            requestStateObj={props.requestStateObj}
                            activeItem={props.activeItem}
                            offers={[]}
                            places={[]}
                            regions={props.regions}
                            removeRegionTag={props.removeRegionTag}
                            vendors={[]}
                            getFieldProps={props.getFieldProps}
                        />
                    )}
                />
            </div>
        );
    };

    return (
        <div className={searchFilterWrapperStyle}>
            <button type="button" onClick={() => setMobileSearchActive(true)} className={mobileInputTriggerStyle}>
                <div className={iconWrapperStyle}>
                    <MapMarkerOutlineIcon size="2" className={inputIconSearchIconStyle} />
                </div>

                {selectedRegion ? (
                    <Pill size="small" className={selectedRegionPillStyle}>
                        {selectedRegion.name}
                    </Pill>
                ) : (
                    placeholder
                )}

                <ChevronDownIcon size="2" className={arrowIconStyle} />
            </button>

            <MobileOptionsModal isOpen={isMobileSearchActive} modalHeading="Wyszukiwanie" onModalClose={() => setMobileSearchActive(false)}>
                {renderSearchAutocomplete(setMobileSearchActive, true)}
            </MobileOptionsModal>

            <DesktopBlend isActive={isDesktopSearchBlendActive} isFullScreen={false} top="15rem" heightDecrease="(13rem + 60px)" />

            <div className={searchDesktopWrapperStyle}>{renderSearchAutocomplete(setDesktopSearchBlendActive)}</div>
        </div>
    );
};

export const PromotionListSearchFilter = connect(mapStateToProps, mapActionsToProps)(PromotionListSearchFilterC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as IPromotionSearchFormValuesType,
        latestQuery: state.promotionList.latestQuery,
        promotionType: state.promotionList.form.formValues.type as PromotionType | undefined
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                fetchPromotionSearchList,
                updatePromotionRegionList
            },
            dispatch
        )
    };
}

const searchFilterWrapperStyle = css({
    backgroundColor: "var(--colors-white)"
});

const searchDesktopWrapperStyle = css({
    position: "relative",
    display: "none",
    md: {
        display: "flex",
        alignItems: "center",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap",
        mx: "auto",
        my: 0
    },
    lg: {
        flexWrap: "nowrap",
        overflowX: "visible"
    }
});

const mobileInputTriggerStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexBasis: "100%",
    width: "100%",
    height: "5rem",
    px: 2,
    pl: 5,
    pr: 5,
    border: "1px solid var(--colors-gray-600)",
    borderRadius: 1,
    appearance: "none",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    fontSize: "1.4rem",
    textAlign: "left",
    cursor: "text",
    md: {
        display: "none"
    }
});

const iconWrapperStyle = css({
    position: "absolute",
    top: "1.25rem",
    left: "2.2rem",
    zIndex: 1,
    cursor: "text"
});

const inputIconSearchIconStyle = css({
    fill: "var(--colors-secondary)"
});

const selectedRegionPillStyle = css({
    ml: 1.5
});

const arrowIconStyle = css({
    position: "absolute",
    right: 2,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--colors-gray-700)",
    fill: "var(--colors-gray-700)",
    pointerEvents: "none"
});

const dropdownWrapperStyle = css({
    boxSizing: "border-box"
});

const searchAutocompleteWrapperStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    height: "calc(100% - 6rem)",
    sm: {
        p: 0
    },
    md: {
        width: "100%",
        maxHeight: "100%"
    },
    lg: {
        width: "calc(50% - 2rem)"
    }
});
import React, {useState} from "react";
import {connect} from "react-redux";
import {css} from "@panda/css";
import {bindActionCreators, Dispatch} from "redux";

import {ChevronDownIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {FormActions} from "@pg-mono/form";
import {head} from "@pg-mono/nodash";

import {IRPStore} from "../../../../app/rp_reducer";
import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {searchFormActions} from "../../../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../../../search/actions/set_active_dropdown_item";
import {ISearchInputValue} from "../../../../search/components/ISearchInputValue";
import {SearchAutocomplete} from "../../../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../../../search/constants/search_form_constants";
import {SearchTab} from "../../../../search/types/SearchTab";
import {IPromotionRegion, updatePromotionRegionList} from "../../../actions/fetch_promotion_list_region";
import {fetchPromotionSearchList} from "../../../actions/fetch_promotion_search_lists";
import {PromotionType} from "../../../helpers/PromotionType";
import {usePromotionListFilterChange} from "../../hooks/usePromotionListFilterChange";
import {PromotionListSearchFilterAutocompleteDropdown} from "./PromotionListSearchFilterAutocompleteDropdown";

/* eslint-disable @typescript-eslint/no-explicit-any */
export type IPromotionSearchFormValuesType = Record<"search", any>;

interface IStateProps {
    formValues: IPromotionSearchFormValuesType;
    latestQuery: Record<string, string | string[]>;
    promotionType: PromotionType | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchPromotionSearchList: (searchInput: string) => Promise<SearchTab>;
    updatePromotionRegionList: (region: IPromotionRegion | null) => {type: string; result: IPromotionRegion | null};
}

interface IPromotionListSearchFilter extends IStateProps, IActionsProps {}

const PromotionListSearchFilterC = (props: IPromotionListSearchFilter) => {
    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);

    const onPromotionListFilterChange = usePromotionListFilterChange();

    const onSubmit = async (formValues: IPromotionSearchFormValuesType) => {
        const searchValue = formValues["search"] as ISearchInputValue;

        if (searchValue.tabType === SearchTab.Regions) {
            onPromotionListFilterChange(props.promotionType || undefined, searchValue.regions?.[0]);
        }
    };

    const onAfterChange = (name?: string, value?: ISearchInputValue | number | undefined) => {
        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;

        //  submit only if user selected new region
        if (props.latestQuery.region === currentValues.search.regions?.[0]?.id) {
            return;
        }

        props.updatePromotionRegionList(head(currentValues.search?.regions) || null);

        return onSubmit(currentValues);
    };

    const onSearchSubmit = () => onSubmit(props.formValues);

    const selectedRegion = props.formValues?.search?.regions?.[0];
    const placeholder = "Miasto lub region";

    const renderSearchAutocomplete = (onDropdownStatusChange: React.Dispatch<React.SetStateAction<boolean>>, isInModal?: boolean) => {
        return (
            <div className={searchAutocompleteWrapperStyle}>
                <SearchAutocomplete
                    values={props.formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={onSearchSubmit}
                    onDropdownStatusChange={onDropdownStatusChange}
                    onAfterTabSwitch={props.resetActiveDropdownItem}
                    placeholder={placeholder}
                    disableMultiRegion
                    fetchAllSearchLists={props.fetchPromotionSearchList}
                    isInModal={isInModal}
                    autoFocus
                    dropdownWrapperClassName={dropdownWrapperStyle}
                    minWidth="100%"
                    renderDropdownList={(props) => (
                        <PromotionListSearchFilterAutocompleteDropdown
                            dropdownIsOpen={props.dropdownIsOpen}
                            search={props.search}
                            selectedTab={props.selectedTab}
                            onLinkClick={props.onLinkClick}
                            requestStateObj={props.requestStateObj}
                            activeItem={props.activeItem}
                            offers={[]}
                            places={[]}
                            regions={props.regions}
                            removeRegionTag={props.removeRegionTag}
                            vendors={[]}
                            getFieldProps={props.getFieldProps}
                        />
                    )}
                />
            </div>
        );
    };

    return (
        <div className={searchFilterWrapperStyle}>
            <button type="button" onClick={() => setMobileSearchActive(true)} className={mobileInputTriggerStyle}>
                <div className={iconWrapperStyle}>
                    <MapMarkerOutlineIcon size="2" className={inputIconSearchIconStyle} />
                </div>

                {selectedRegion ? (
                    <Pill size="small" className={selectedRegionPillStyle}>
                        {selectedRegion.name}
                    </Pill>
                ) : (
                    placeholder
                )}

                <ChevronDownIcon size="2" className={arrowIconStyle} />
            </button>

            <MobileOptionsModal isOpen={isMobileSearchActive} modalHeading="Wyszukiwanie" onModalClose={() => setMobileSearchActive(false)}>
                {renderSearchAutocomplete(setMobileSearchActive, true)}
            </MobileOptionsModal>

            <DesktopBlend isActive={isDesktopSearchBlendActive} isFullScreen={false} top="15rem" heightDecrease="(13rem + 60px)" />

            <div className={searchDesktopWrapperStyle}>{renderSearchAutocomplete(setDesktopSearchBlendActive)}</div>
        </div>
    );
};

export const PromotionListSearchFilter = connect(mapStateToProps, mapActionsToProps)(PromotionListSearchFilterC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as IPromotionSearchFormValuesType,
        latestQuery: state.promotionList.latestQuery,
        promotionType: state.promotionList.form.formValues.type as PromotionType | undefined
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                fetchPromotionSearchList,
                updatePromotionRegionList
            },
            dispatch
        )
    };
}

const searchFilterWrapperStyle = css({
    backgroundColor: "var(--colors-white)"
});

const searchDesktopWrapperStyle = css({
    position: "relative",
    display: "none",
    md: {
        display: "flex",
        alignItems: "center",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap",
        mx: "auto",
        my: 0
    },
    lg: {
        flexWrap: "nowrap",
        overflowX: "visible"
    }
});

const mobileInputTriggerStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexBasis: "100%",
    width: "100%",
    height: "5rem",
    px: 2,
    pl: 5,
    pr: 5,
    border: "1px solid var(--colors-gray-600)",
    borderRadius: 1,
    appearance: "none",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    fontSize: "1.4rem",
    textAlign: "left",
    cursor: "text",
    md: {
        display: "none"
    }
});

const iconWrapperStyle = css({
    position: "absolute",
    top: "1.25rem",
    left: "2.2rem",
    zIndex: 1,
    cursor: "text"
});

const inputIconSearchIconStyle = css({
    fill: "var(--colors-secondary)"
});

const selectedRegionPillStyle = css({
    ml: 1.5
});

const arrowIconStyle = css({
    position: "absolute",
    right: 2,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--colors-gray-700)",
    fill: "var(--colors-gray-700)",
    pointerEvents: "none"
});

const dropdownWrapperStyle = css({
    boxSizing: "border-box"
});

const searchAutocompleteWrapperStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    height: "calc(100% - 6rem)",
    sm: {
        p: 0
    },
    md: {
        width: "100%",
        maxHeight: "100%"
    },
    lg: {
        width: "calc(50% - 2rem)"
    }
});
